<template>
  <div v-if="shouldShow" id="debug-theme-switcher">
    <div id="debug-theme-switcher-container">
      <div class="debug-theme-switcher-label">Developer options</div>
      <div class="debug-theme-switcher-item">
        <label for="theme-select">Theme:</label>
        <select id="theme-select" v-model="selectedTheme">
          <option value="classic">Classic</option>
          <option value="modern">Modern</option>
          <option value="arbus">Arbus</option>
          <option value="lovelace">Lovelace</option>
        </select>
      </div>
      <div class="debug-theme-switcher-item">
        <label for="variant-select">Variant:</label>
        <select id="variant-select" v-model="selectedVariant">
          <option value="standard">Standard</option>
          <option value="embedded">Embedded</option>
        </select>
      </div>
      <div class="debug-theme-switcher-item">
        <label for="status-select">Status:</label>
        <select id="status-select" v-model="selectedStatus">
          <option value="">Default ({{ newsletter.status }})</option>
          <option value="active">Active</option>
          <option value="deleting">Deleting</option>
          <option value="disabled">Disabled</option>
          <option value="unconfirmed">Unconfirmed</option>
          <option value="permanently_approved">Permanently Active</option>
          <option value="permanently_rejected">Permanently Disabled</option>
          <option value="undeliverable">Undeliverable</option>
          <option value="staging">Staging</option>
          <option value="prospective">Prospective</option>
          <option value="dormant">Dormant</option>
        </select>
      </div>
      <div class="debug-theme-switcher-item">
        <label for="locale-select">Locale:</label>
        <select id="locale-select" v-model="selectedLocale">
          <option value="">Default ({{ newsletter.locale }})</option>
          <option value="en">English</option>
          <option value="fr">French</option>
          <option value="de">German</option>
          <option value="es">Spanish</option>
          <option value="tr">Turkish</option>
          <option value="pt-BR">Portuguese (Brazil)</option>
          <option value="pt-PT">Portuguese (Portugal)</option>
          <option value="nl-NL">Dutch</option>
          <option value="pl-PL">Polish</option>
          <option value="cs-CZ">Czech</option>
        </select>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, onMounted, onUnmounted, ref, watch } from "vue";

import type { Newsletter } from "@/types/newsletter";

const props = defineProps<{
  newsletter: Newsletter;
}>();

// Check if we should show the debug switcher
const isDebug = computed(() => {
  return (
    typeof window !== "undefined" &&
    (window as any).DEBUG === true &&
    (window as any).IS_STAFF === true
  );
});

const isHidden = ref(false);
const shouldShow = computed(() => isDebug.value && !isHidden.value);

const handleKeydown = (e: KeyboardEvent) => {
  if (e.metaKey && e.key === "b") {
    e.preventDefault();
    isHidden.value = !isHidden.value;
  }
};
onMounted(() => window.addEventListener("keydown", handleKeydown));
onUnmounted(() => window.removeEventListener("keydown", handleKeydown));

// Get initial values from URL params
const urlParams = new URLSearchParams(window.location.search);
const themeFromUrl = urlParams.get("theme");
const variantFromUrl =
  urlParams.get("as_embed") === "true" ? "embedded" : "standard";
const statusFromUrl = urlParams.get("debug_status");
const localeFromUrl = urlParams.get("debug_locale");

const selectedTheme = ref(themeFromUrl || props.newsletter.archive_theme);
const selectedVariant = ref(variantFromUrl || "standard");
const selectedStatus = ref(statusFromUrl || "");
const selectedLocale = ref(localeFromUrl || "");

// Watch for changes and update URL
watch(selectedTheme, (newValue) => {
  const url = new URL(window.location.href);
  url.searchParams.set("theme", newValue);
  window.location.href = url.toString();
});

watch(selectedVariant, (newValue) => {
  const url = new URL(window.location.href);
  url.searchParams.set("as_embed", newValue === "embedded" ? "true" : "false");
  window.location.href = url.toString();
});

watch(selectedStatus, (newValue) => {
  const url = new URL(window.location.href);
  if (newValue) {
    url.searchParams.set("debug_status", newValue);
  } else {
    url.searchParams.delete("debug_status");
  }
  window.location.href = url.toString();
});

watch(selectedLocale, (newValue) => {
  const url = new URL(window.location.href);
  if (newValue) {
    url.searchParams.set("debug_locale", newValue);
  } else {
    url.searchParams.delete("debug_locale");
  }
  window.location.href = url.toString();
});
</script>

<style lang="css">
body:has(#debug-theme-switcher) {
  padding-top: 32px;
  box-sizing: border-box;
}
#debug-theme-switcher {
  position: fixed;
  top: 0rem;
  border-bottom: 1px solid rgb(250 204 21 / 0.5);
  left: 0rem;
  right: 0rem;
  background-color: rgb(250 204 21 / 0.5);
  background-image: repeating-linear-gradient(
    -45deg,
    rgba(0, 0, 0, 0.05),
    rgba(0, 0, 0, 0.05) 20px,
    transparent 20px,
    transparent 40px
  );
  z-index: 1000;
  padding: 0.5rem 1rem;
  font-size: var(--font-size-extra-small);
  font-family: "IBM Plex Mono", sans-serif;
}

.debug-theme-switcher-label {
  font-weight: 600;
  white-space: nowrap;
  text-transform: uppercase;
  width: 30vw;
}

#debug-theme-switcher-container {
  display: flex;
  align-items: center;
  gap: 8px;
}

#debug-theme-switcher label {
  font-weight: 600;
  white-space: nowrap;
  display: flex;
  flex-direction: column;
  display: none;
}

.debug-theme-switcher-item {
  display: flex;
  flex: 1;
  flex-direction: column;
  align-items: center;
  justify-content: stretch;
  gap: 8px;
}

#debug-theme-switcher select {
  width: 100%;
  padding: 4px 12px;
  background-color: var(--color-background);
  border: 1px solid var(--color-background-offset);
  border-radius: 9999px;
  cursor: pointer;
  font-size: var(--font-size-extra-small);
  color: var(--color-foreground);
  font-family: var(--font-brand);
}

#debug-theme-switcher select:focus {
  outline: 2px solid var(--color-action);
  outline-offset: 2px;
}
</style>
